import React, { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import moment from "moment";

import MUIKeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import MUIRotateLeftIcon from '@mui/icons-material/RotateLeft';

import MUIButton from "@mui/material/Button";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUITable from '@mui/material/Table';
import MUITypography from "@mui/material/Typography";
import MUITableBody from '@mui/material/TableBody';
import MUITableCell from '@mui/material/TableCell';
import MUITableContainer from '@mui/material/TableContainer';
import MUITableHead from '@mui/material/TableHead';
import MUITableRow from '@mui/material/TableRow';
import MUIPaper from "@mui/material/Paper";
import MUIBox from "@mui/material/Box";
import MUITooltip from "@mui/material/Tooltip";
import MUILocalShippingIcon from '@mui/icons-material/LocalShipping';
import MUICheckIcon from "@mui/icons-material/Check";

import Card from "../../Card";
import EmptyState from "../../EmptyState";
import Location from "../../../Utility/Crud/Location";
import Term from "../../../Utility/Crud/Term";
import InfoItem from "../../InfoItem";
import Security from "../../../Utility/Security";
import ServicePartCatalog from "../../../Utility/Crud/ServicePartCatalog";
import Menu from "../../Menu";

/**
 * CardChartPartReturn renders a Card component
 * displaying part return information.
 * @returns {JSX.Element}
 */

const title = "Part Return";
const icon = MUIKeyboardReturnIcon;

const className = "CardChartPartReturn";
const menuColumnSx = { width: 56, minWidth: 56, maxWidth: 56, p: 1 };

export default function CardChartPartReturn(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);
    const [cardChart, setCardChart] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const serviceQuoteParts = Object.values(chartContext.chart.serviceQuotePart || {}).filter(part => {
        return part.orderActivityId === chartContext.orderId;
    }) || [].sort((a, b) => new Date(b.followupDate) - new Date(a.followupDate));
    const [resetParts, setResetParts] = useState({});

    // Close edit mode if originalChart changes (on save)
    useEffect(() => {
        setEditMode(false);
    }, [chartContext.originalChart]);

    // Sync this card's edit mode with ChartContext for ChartNavigationBar display
    useEffect(() => {
        chartContext.setCardEditMode?.(className, editMode);
        return () => chartContext.setCardEditMode?.(className, false);
    }, [editMode, chartContext.setCardEditMode]);

    /**
     * Resets a part back to its original service location to be fixed.
     * @param {object} row current row clicked
     */
    const handleResetPart = (row) => {
        setResetParts((prev) => {
            return { ...prev, [row.id]: { "reset": true } }
        })
        chartContext.onChange(
            {
                serviceQuotePart:
                {
                    [row.id]:
                    {
                        receivedAt: null,
                        receivedByUserProfileId: null,
                        locationId: chartContext.originalChart.orderActivity[chartContext.orderId]?.serviceLocationId,
                        serviceQuotePartStatusTermId: 66,
                    }
                }
            });
        setCardChart({
            ...cardChart,
            receivedAt: null,
            receivedByUserProfileId: null,
            locationId: chartContext.originalChart.orderActivity[chartContext.orderId]?.serviceLocationId,
            serviceQuotePartStatusTermId: 66,
        });
    }

    const getTrackingLink = (trackingNumber) => {
        const patterns = {
            UPS: /^1Z[0-9A-Z]{16}$/,
            FedEx: /^(96\d{20}|\d{15}|\d{12})$/,
            USPS: /^(94|93|92|95)[0-9]{20}$|^[A-Z]{2}[0-9]{9}US$/,
            DHL: /^\d{10}$/,
        };

        if (patterns.UPS.test(trackingNumber)) {
            return `https://www.ups.com/track?tracknum=${trackingNumber}`;
        } else if (patterns.FedEx.test(trackingNumber)) {
            return `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`;
        } else if (patterns.USPS.test(trackingNumber)) {
            return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
        } else if (patterns.DHL.test(trackingNumber)) {
            return `https://www.dhl.com/en/express/tracking.html?AWB=${trackingNumber}`;
        } else {
            return null;
        }
    }

    /**
     * Render all invoice data and claim status dropdown
     */
    const renderServiceQuotePartTable = () => {
        return (
            serviceQuoteParts.length > 0 ? (
                <MUITableContainer component={MUIPaper} sx={{ mt: 2 }}>
                    <MUITable stickyHeader size="small">
                        <MUITableHead>
                            <MUITableRow>
                                <MUITableCell colSpan={3}>Part Name</MUITableCell>
                                <MUITableCell colSpan={1}>Warranty</MUITableCell>
                                <MUITableCell colSpan={1}>Qty</MUITableCell>
                                <MUITableCell colSpan={2}>Status/Reason</MUITableCell>
                                <MUITableCell colSpan={2}>Location</MUITableCell>
                                <MUITableCell colSpan={1} sx={menuColumnSx}>&nbsp;</MUITableCell>
                            </MUITableRow>
                        </MUITableHead>
                        <MUITableBody>
                            {serviceQuoteParts && serviceQuoteParts.length > 0 ? (
                                serviceQuoteParts.map((serviceQuotePart, idx) => {
                                    const trackingUrl = getTrackingLink(serviceQuotePart.trackingNumber);
                                    return (
                                        <React.Fragment key={idx}>
                                            <MUITableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <MUITableCell colSpan={3} sx={{ padding: 2 }}>{ServicePartCatalog.getDisplayName(chartContext.chart.servicePartCatalog[serviceQuotePart.servicePartCatalogId ?? null]) || "N/A"}</MUITableCell>
                                                <MUITableCell colSpan={1}>{serviceQuotePart.warranty ? "Yes" : "No"}</MUITableCell>
                                                <MUITableCell colSpan={1}>{serviceQuotePart.quantity ? serviceQuotePart.quantity : null}</MUITableCell>
                                                <MUITableCell colSpan={2}>
                                                    {serviceQuotePart.receivedAt ? (
                                                        <MUITooltip
                                                            placement="left"
                                                            title={
                                                                serviceQuotePart.receivedByUserProfileId
                                                                    ? `Received at ${moment
                                                                        .utc(serviceQuotePart.receivedAt)
                                                                        .format("M/D/YYYY")} by ${mainContext.usersIndexed[serviceQuotePart.receivedByUserProfileId]
                                                                            .firstname
                                                                    } ${mainContext.usersIndexed[serviceQuotePart.receivedByUserProfileId]
                                                                        .lastname
                                                                    }`
                                                                    : `Received at ${moment
                                                                        .utc(serviceQuotePart.receivedAt)
                                                                        .format("M/D/YYYY")}`
                                                            }
                                                        >
                                                            <MUIBox display="inline-flex" alignItems="center">
                                                                {serviceQuotePart.serviceQuotePartStatusTermId ? (
                                                                    <span style={{ mr: 4 }}>
                                                                        <MUIBox component="span" sx={{ mr: 1 }}>
                                                                            {Term.getDisplayName(mainContext.termsIndexed[serviceQuotePart.serviceQuotePartStatusTermId])}
                                                                            {serviceQuotePart.serviceQuotePartReasonTermId ? (
                                                                                <MUIBox component="div">
                                                                                    {Term.getDisplayName(mainContext.termsIndexed[serviceQuotePart.serviceQuotePartReasonTermId])}
                                                                                </MUIBox>
                                                                            ) : null}
                                                                        </MUIBox>
                                                                    </span>
                                                                ) : (
                                                                    null
                                                                )}
                                                                <MUICheckIcon
                                                                    fontSize="small"
                                                                    sx={{
                                                                        color: (theme) => theme.palette.success.main,
                                                                    }}
                                                                />
                                                            </MUIBox>
                                                        </MUITooltip>
                                                    ) : serviceQuotePart.serviceQuotePartStatusTermId ? (
                                                        <>
                                                            {Term.getDisplayName(mainContext.termsIndexed[serviceQuotePart.serviceQuotePartStatusTermId])}
                                                            {serviceQuotePart.serviceQuotePartReasonTermId ? (
                                                                    <MUIBox component="div">
                                                                        <MUITypography variant="caption">
                                                                            {Term.getDisplayName(mainContext.termsIndexed[serviceQuotePart.serviceQuotePartReasonTermId])}
                                                                        </MUITypography>
                                                                </MUIBox>
                                                            ) : null}
                                                        </>
                                                    ) : (
                                                        null
                                                    )}
                                                </MUITableCell>
                                                <MUITableCell colSpan={2}>
                                                    <MUITypography variant="Body">
                                                        {Location.getDisplayName(mainContext.locationsIndexed[serviceQuotePart.locationId ?? null])}
                                                    </MUITypography>
                                                </MUITableCell>
                                                <MUITableCell colSpan={1} sx={menuColumnSx}>
                                                    <MUIBox display="inline-flex" alignItems="center" justifyContent="center" width="100%">
                                                        {editMode && Security.hasPermission("chart.resetParts") ? (
                                                            <Menu
                                                                menuItems={[
                                                                    <MUIMenuItem disabled={!Security.hasPermission("chart.resetParts") || resetParts[serviceQuotePart.id]?.reset === true} onClick={() => handleResetPart(serviceQuotePart)}>
                                                                        <MUIListItemIcon>
                                                                            <MUIRotateLeftIcon fontSize="small" />
                                                                        </MUIListItemIcon>
                                                                        Reset to Local Office
                                                                    </MUIMenuItem>
                                                                ]}
                                                            />
                                                        ) : null}
                                                    </MUIBox>
                                                </MUITableCell>
                                            </MUITableRow>

                                            {/* Additional Fields for RMA # and Tracking # */}
                                            {(serviceQuotePart.rmaNumber || serviceQuotePart.trackingNumber) &&
                                                (serviceQuotePart.serviceQuotePartStatusTermId === 67 || serviceQuotePart.serviceQuotePartStatusTermId === 68) ? (
                                                <MUITableRow sx={{ backgroundColor: (theme) => theme.palette.background.default }}>
                                                    <MUITableCell colSpan={3}></MUITableCell>
                                                    <MUITableCell colSpan={1}></MUITableCell>
                                                    <MUITableCell colSpan={1}></MUITableCell>
                                                    {serviceQuotePart.rmaNumber ? (
                                                        <MUITableCell colSpan={2}>
                                                            <MUITypography variant="subtitle2">
                                                                RMA #
                                                            </MUITypography>
                                                            <MUITypography variant="body" noWrap>
                                                                {serviceQuotePart.rmaNumber}
                                                            </MUITypography>
                                                        </MUITableCell>
                                                    ) : null}
                                                    {serviceQuotePart.trackingNumber ? (
                                                        <MUITableCell colSpan={2}>
                                                            <MUITypography variant="subtitle2">
                                                                Tracking #
                                                            </MUITypography>
                                                                <MUITypography variant="body" noWrap>
                                                                    {serviceQuotePart.trackingNumber}
                                                                    {trackingUrl ? (
                                                                        <MUITooltip title="Track Shipment">
                                                                            <MUIButton
                                                                                onClick={() => {
                                                                                    window.open(trackingUrl, '_blank');
                                                                                }}
                                                                                sx={{
                                                                                    minWidth: 0,
                                                                                    padding: 0,
                                                                                    pl: 1,
                                                                                    color: (theme) => theme.palette.success.main,
                                                                                }}
                                                                            >
                                                                                <MUILocalShippingIcon fontSize="small" />
                                                                            </MUIButton>
                                                                        </MUITooltip>
                                                                    ) : null}
                                                                </MUITypography>
                                                        </MUITableCell>
                                                    ) : null}
                                                        <MUITableCell colSpan={1}></MUITableCell>
                                                </MUITableRow>
                                            ) : null}
                                        </React.Fragment>
                                    )
                                })
                            ) : null}
                        </MUITableBody>
                    </MUITable>
                </MUITableContainer>
            ) : (
                <EmptyState
                    line1="Nothing to show"
                    line2="Better luck next time!"
                />
            )
        )
    }

    /**
     * Renders the read-only view displaying part return information.
     * @returns {JSX.Element} Grid layout with the part return information
    */
    const renderContent = () => {
        return (
            renderServiceQuotePartTable()
        );
    };

    /**
        * Returns the action buttons for the card component.
        * @returns {Array} Array of action button components
        */
    const getActions = () => {
        const handleCancel = () => {
            const serviceQuotePart = {};
            Object.values(chartContext.chart.serviceQuotePart || {}).filter(part => {
                if (part.orderActivityId === chartContext.orderId) {
                    serviceQuotePart[part.id] = part;
                }
            });

            const serviceQuoteFields = cardChart ? Object.keys(cardChart) : [];

            Object.values(serviceQuotePart || []).forEach((part) => {
                for (const field of serviceQuoteFields) {
                    if (serviceQuotePart[part.id]?.[field] !== chartContext.originalChart.serviceQuotePart[part.id]?.[field]) {
                        serviceQuotePart[part.id][field] = chartContext.originalChart.serviceQuotePart[part.id][field]
                    }
                }
                chartContext.onChange({ serviceQuotePart: { [part.id]: { ...serviceQuotePart[part.id] } } });
                setResetParts({});
                setEditMode(false);
            });
        }
        if (Object.values(serviceQuoteParts).length === 0) {
            return [];
        }
        return [
            ...(editMode ? [
                <MUIButton
                    color="error"
                    disabled={chartContext.isUpdating}
                    onClick={handleCancel}
                    size="small"
                >
                    Cancel
                </MUIButton>
            ] : [
                <MUIButton
                    disabled={chartContext.isUpdating}
                    onClick={() => setEditMode(true)}
                    size="small"
                >
                    Edit
                </MUIButton>
            ])
        ];
    }

    return (
        <Card
            renderContent={renderContent}
            getActions={getActions}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}

CardChartPartReturn.meta = { className: className, title: title, icon: icon };
